/**
 * EmployeeTimeDrawer
 *
 * Side-Drawer-Dialog: zeigt fuer eine:n ausgewaehlten Mitarbeiter:in den
 * gesamten Zeitraum als Tages-Strip mit Farb-Codierung (gearbeitet / Urlaub /
 * krank / Feiertag / fehlt / Wochenende) und einer Wochen-Bilanz Soll vs Ist.
 *
 * Datenquellen sind bereits im Parent (`TimeTrackingOverview`) berechnet:
 *   - `emp.dayStatusMap` aus `buildDayStatusMap` (utils.js)
 *   - `emp.expectedMinutes`, `emp.totalNet`, `emp.difference`
 *   - `holidays` als Liste fuer Feiertagsnamen
 */
import React, { useMemo, useState } from 'react';
import { format, parseISO, startOfISOWeek, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Palmtree, Stethoscope, Calendar, CheckCircle2, AlertCircle, Briefcase, Heart, MinusCircle, MessageSquarePlus } from 'lucide-react';
import { DAY_TYPES, DAY_TYPE_LABELS, DAY_TYPE_STYLE, formatMinutes } from './utils';
import { formatLastFirst } from '../../lib/nameFormat';
import CorrectionRequestDialog from './CorrectionRequestDialog';

const TYPE_ICON = {
  [DAY_TYPES.WORKED]: CheckCircle2,
  [DAY_TYPES.PARTIAL]: AlertCircle,
  [DAY_TYPES.MISSING]: MinusCircle,
  [DAY_TYPES.VACATION]: Palmtree,
  [DAY_TYPES.VACATION_HALF]: Palmtree,
  [DAY_TYPES.SICK]: Stethoscope,
  [DAY_TYPES.ABSENCE_OTHER]: Heart,
  [DAY_TYPES.HOLIDAY]: Calendar,
  [DAY_TYPES.WEEKEND]: Briefcase,
  [DAY_TYPES.FUTURE]: Briefcase,
};

function groupByWeek(days) {
  const groups = new Map();
  days.forEach((d) => {
    const dt = parseISO(d.date);
    const weekStart = startOfISOWeek(dt);
    const key = format(weekStart, 'yyyy-MM-dd');
    if (!groups.has(key)) {
      groups.set(key, {
        weekStart,
        kw: getISOWeek(dt),
        days: [],
      });
    }
    groups.get(key).days.push(d);
  });
  return Array.from(groups.values());
}

export default function EmployeeTimeDrawer({ open, onOpenChange, emp, holidays, onCorrectionSubmitted }) {
  const [correctionDay, setCorrectionDay] = useState(null);
  const dayList = useMemo(() => {
    if (!emp?.dayStatusMap) return [];
    return Object.values(emp.dayStatusMap).sort((a, b) => a.date.localeCompare(b.date));
  }, [emp]);

  const weeks = useMemo(() => groupByWeek(dayList), [dayList]);

  const holidayMap = useMemo(() => {
    const map = {};
    (holidays || []).forEach((h) => { map[h.date] = h.name || h.label || 'Feiertag'; });
    return map;
  }, [holidays]);

  if (!emp) return null;

  const dailyMinutes = ((emp.weekly_hours || 40) * 60) / 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[min(95vw,1100px)] max-h-[90vh] overflow-hidden flex flex-col" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {formatLastFirst(emp)}
            <Badge variant="outline" className="font-normal">{emp.employee_number}</Badge>
          </DialogTitle>
          <DialogDescription>
            Tagesweise Übersicht mit Urlaub, Abwesenheit und Feiertagen — Soll wird automatisch reduziert.
          </DialogDescription>
        </DialogHeader>

        {/* Top-Bilanz */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 py-3 border-b border-stone-200">
          <div>
            <p className="text-xs text-muted-foreground">Soll (Zeitraum)</p>
            <p className="font-mono font-semibold text-base">{formatMinutes(Math.round(emp.expectedMinutes))}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Ist (netto)</p>
            <p className="font-mono font-semibold text-base">{formatMinutes(emp.totalNet)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Differenz</p>
            <p className={`font-mono font-semibold text-base ${emp.difference >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
              {emp.difference >= 0 ? '+' : ''}{formatMinutes(Math.round(emp.difference))}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Urlaub</p>
            <p className="font-mono font-semibold text-base text-sky-700">{emp.vacationDays || 0}</p>
            {emp.vacationDaysHalf > 0 && (
              <p className="text-[10px] text-muted-foreground">davon {emp.vacationDaysHalf}× halbtags</p>
            )}
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Krank / sonst.</p>
            <p className="font-mono font-semibold text-base">
              <span className="text-orange-700">{emp.sickDays || 0}</span>
              <span className="text-muted-foreground"> · </span>
              <span className="text-purple-700">{emp.absenceOtherDays || 0}</span>
            </p>
          </div>
        </div>

        {/* Wochenstrip */}
        <div className="flex-1 overflow-auto py-3 space-y-4">
          {weeks.map((w) => {
            const workedWeek = w.days.reduce((s, d) => s + (d.workedMinutes || 0), 0);
            const expectedDaysInWeek = w.days.filter((d) =>
              d.type !== DAY_TYPES.WEEKEND
              && d.type !== DAY_TYPES.HOLIDAY
              && d.type !== DAY_TYPES.VACATION
              && d.type !== DAY_TYPES.SICK
              && d.type !== DAY_TYPES.ABSENCE_OTHER
            ).length;
            const expectedHalfDays = w.days.filter((d) => d.type === DAY_TYPES.VACATION_HALF).length;
            const expectedWeekMinutes = expectedDaysInWeek * dailyMinutes + expectedHalfDays * dailyMinutes * 0.5;
            const diffWeek = workedWeek - expectedWeekMinutes;

            return (
              <div key={format(w.weekStart, 'yyyy-MM-dd')} className="rounded-lg border border-stone-200 p-3 bg-white">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-stone-700">
                    KW {w.kw} <span className="text-xs text-stone-500 font-normal">· ab {format(w.weekStart, 'dd.MM.yyyy', { locale: de })}</span>
                  </div>
                  <div className="text-xs flex items-center gap-3">
                    <span>Ist <strong className="font-mono">{formatMinutes(workedWeek)}</strong></span>
                    <span className="text-muted-foreground">/ Soll <strong className="font-mono">{formatMinutes(Math.round(expectedWeekMinutes))}</strong></span>
                    <span className={`font-mono ${diffWeek >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {diffWeek >= 0 ? '+' : ''}{formatMinutes(Math.round(diffWeek))}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {w.days.map((d) => {
                    const dt = parseISO(d.date);
                    const Icon = TYPE_ICON[d.type] || Briefcase;
                    const cls = DAY_TYPE_STYLE[d.type] || 'bg-stone-50 border-stone-200 text-stone-500';
                    const label = DAY_TYPE_LABELS[d.type] || '';
                    const hName = holidayMap[d.date];
                    const tooltip = `${format(dt, 'EEEE, dd.MM.yyyy', { locale: de })} — ${hName || label}${d.workedMinutes ? ` · ${formatMinutes(d.workedMinutes)}` : ''}`;
                    const canRequestCorrection =
                      d.type === DAY_TYPES.MISSING || d.type === DAY_TYPES.PARTIAL || d.type === DAY_TYPES.WORKED;
                    return (
                      <div
                        key={d.date}
                        className={`relative group rounded-md border ${cls} px-2 py-1.5 min-h-[60px] flex flex-col items-center justify-center text-center`}
                        title={tooltip}
                        data-testid={`drawer-day-${emp.id}-${d.date}`}
                      >
                        <span className="text-[10px] leading-tight">
                          {format(dt, 'EEE dd.MM.', { locale: de })}
                        </span>
                        <Icon className="h-4 w-4 mt-1 opacity-80" />
                        {d.workedMinutes > 0 && (
                          <span className="text-[10px] font-mono mt-0.5">
                            {formatMinutes(d.workedMinutes)}
                          </span>
                        )}
                        {hName && d.type === DAY_TYPES.HOLIDAY && (
                          <span className="text-[9px] mt-0.5 line-clamp-1">{hName}</span>
                        )}
                        {canRequestCorrection && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setCorrectionDay(d); }}
                            className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 hover:bg-white border border-stone-300 rounded p-0.5 shadow-sm"
                            title="Korrekturantrag für diesen Tag stellen"
                            data-testid={`drawer-day-correction-${emp.id}-${d.date}`}
                          >
                            <MessageSquarePlus className="h-3 w-3 text-primary" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                  {/* Bei kuerzeren letzten/ersten Wochen leere Plaetze auffuellen */}
                  {Array.from({ length: 7 - w.days.length }).map((_, i) => (
                    <div key={`pad-${i}`} className="rounded-md border border-dashed border-stone-200 bg-stone-50 min-h-[60px]" />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <CorrectionRequestDialog
          open={!!correctionDay}
          onOpenChange={(o) => { if (!o) setCorrectionDay(null); }}
          employeeId={emp.id}
          employeeName={formatLastFirst(emp)}
          date={correctionDay?.date}
          timeEntryId={correctionDay?.timeEntryId || null}
          defaultClockIn=""
          defaultClockOut=""
          defaultBreakMinutes={0}
          weeklyHours={emp?.weekly_hours || null}
          onSubmitted={() => { onCorrectionSubmitted?.(); }}
        />


        {/* Legende */}
        <div className="border-t border-stone-200 pt-3 flex flex-wrap gap-3 text-[11px] text-stone-600">
          {[
            DAY_TYPES.WORKED, DAY_TYPES.PARTIAL, DAY_TYPES.MISSING,
            DAY_TYPES.VACATION, DAY_TYPES.VACATION_HALF, DAY_TYPES.SICK,
            DAY_TYPES.ABSENCE_OTHER, DAY_TYPES.HOLIDAY, DAY_TYPES.WEEKEND,
          ].map((t) => (
            <span key={t} className="flex items-center gap-1.5">
              <span className={`inline-block w-3 h-3 rounded-sm border ${DAY_TYPE_STYLE[t]}`} />
              {DAY_TYPE_LABELS[t]}
            </span>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
